import { useEffect, useState } from 'react';
import { PhotoIcon, SpeakerWaveIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { attachmentApi } from '../../services/api';
import type { ReportAttachment } from '../../store/reportStore';

// Attachments are never static-served (docs/19 D5) — fetch through the auth
// interceptor as a blob and mint an object URL, revoking it on unmount.
function useAttachmentUrl(id: string): { url: string | null; error: boolean; loading: boolean } {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let revoked = false;
    let objectUrl: string | null = null;
    setLoading(true);
    setError(false);
    attachmentApi
      .getBlob(id)
      .then((res) => {
        if (revoked) return;
        objectUrl = URL.createObjectURL(res.data as Blob);
        setUrl(objectUrl);
      })
      .catch(() => !revoked && setError(true))
      .finally(() => !revoked && setLoading(false));

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id]);

  return { url, error, loading };
}

function PhotoThumb({ att, onOpen }: { att: ReportAttachment; onOpen: (url: string) => void }) {
  const { url, error, loading } = useAttachmentUrl(att.id);
  if (error) {
    return (
      <div className="w-16 h-16 rounded border border-command-border bg-command-bg flex items-center justify-center text-gray-600">
        <PhotoIcon className="w-6 h-6" />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => url && onOpen(url)}
      className="w-16 h-16 rounded border border-command-border overflow-hidden bg-command-bg hover:border-command-accent transition-colors"
      aria-label="Open photo"
    >
      {loading || !url ? (
        <div className="w-full h-full animate-pulse bg-command-border/40" />
      ) : (
        <img src={url} alt="Report attachment" className="w-full h-full object-cover" />
      )}
    </button>
  );
}

function AudioPlayer({ att }: { att: ReportAttachment }) {
  const { url, error, loading } = useAttachmentUrl(att.id);
  if (error) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <SpeakerWaveIcon className="w-4 h-4" /> audio unavailable
      </div>
    );
  }
  if (loading || !url) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <SpeakerWaveIcon className="w-4 h-4" /> loading audio…
      </div>
    );
  }
  return <audio controls src={url} className="h-8 max-w-full" />;
}

export default function AttachmentViewer({ attachments }: { attachments: ReportAttachment[] }) {
  const [lightbox, setLightbox] = useState<string | null>(null);
  if (!attachments || attachments.length === 0) return null;

  const photos = attachments.filter((a) => a.kind === 'PHOTO');
  const audios = attachments.filter((a) => a.kind === 'AUDIO');

  return (
    <div className="mt-3 space-y-2">
      {photos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {photos.map((att) => (
            <PhotoThumb key={att.id} att={att} onOpen={setLightbox} />
          ))}
        </div>
      )}
      {audios.map((att) => (
        <AudioPlayer key={att.id} att={att} />
      ))}

      {lightbox && (
        <div
          className="fixed inset-0 z-[1000] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-command-panel/80 border border-command-border text-gray-200 hover:text-white"
            aria-label="Close"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
          <img
            src={lightbox}
            alt="Report attachment full size"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
